import { CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import Box from "@mui/material/Box";
import { ReportDataModel, ReportModel } from "../../models/report.model";
import { Chart } from "../../models/company.model";
import _ from "lodash";
import { useGroupBy, parseChartExpression, chartHeight } from "../../chart-helpers/group.table.helper";

export default function CustomizedDoughnut(props: { chart: Chart; reports?: ReportDataModel }) {
  const { groupBy } = props.chart || {};
  return (
    <>
      {!groupBy && <CommonDoughnut chart={props.chart} reports={props.reports} />}
      {groupBy && <GroupByDoughnut chart={props.chart} reports={props.reports} />}
    </>
  );
}

function GroupByDoughnut(props: { chart: Chart; reports?: ReportDataModel }) {
  const { loading, groups } = useGroupBy({ ...props, isSingle: true });
  return <CommonDoughnut chart={props.chart} loading={loading} groups={groups[0]} />;
}

function CommonDoughnut(props: { chart: Chart; reports?: ReportDataModel; loading?: boolean; groups?: ReportModel[] }) {
  const { label, data: chartKeys, doughnut } = props.chart || {};
  const { groups } = props;

  const reportData = props.reports?.main || {};
  const labels = (chartKeys || []).map((d) => d.label);
  const datasets = !groups
    ? [
        {
          data: (chartKeys || []).map((d) => parseChartExpression(reportData, d.expression, d.decimalPlaces)),
          backgroundColor: (chartKeys || []).map((d) => d.color),
        },
      ]
    : groups.map((g) => {
        return {
          label: g.groupLabel,
          data: (chartKeys || []).map(
            (key) => parseChartExpression(g, key.expression, key.decimalPlaces) || Number.MIN_VALUE
          ),
          backgroundColor: (chartKeys || []).map((key) =>
            parseChartExpression(g, key.expression, key.decimalPlaces) ? key.color : "rgba(0,0,0,0)"
          ),
          borderColor: "#ccc",
          borderWidth: 1,
        };
      });
  const groupCenterText = groups?.reduce((acc, g) => {
    return doughnut?.center
      ? acc + parseChartExpression(g, doughnut?.center?.expression, doughnut?.center?.decimalPlaces)
      : 0;
  }, 0);
  const cutout = Math.max(75 - datasets.length * 5, 30).toString() + "%";
  return (
    <Stack spacing={2}>
      <Paper elevation={2} className="padding-small">
        <Typography variant={"h6"} textAlign={"center"}>
          {label || "\u00A0"}
        </Typography>
      </Paper>
      <Paper elevation={2} className="padding-normal" sx={{ overflow: "hidden" }}>
        <Box
          className={"all-center-wrapper relative"}
          style={{ height: chartHeight, maxWidth: chartHeight, margin: "auto" }}
        >
          {!props.loading && (
            <>
              <Doughnut
                options={{
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      yAlign: "bottom",
                      xAlign: "center",
                      callbacks: {
                        label: (tooltipItem) => {
                          const decimalPlaces = chartKeys[tooltipItem.dataIndex]?.decimalPlaces || 0;
                          const formattedValue = tooltipItem.parsed.toFixed(decimalPlaces);
                          return tooltipItem.dataset.label ? `${tooltipItem.dataset.label}: ${formattedValue}` : formattedValue;
                        },
                      },
                    },
                  },
                  cutout,
                }}
                data={{
                  labels,
                  datasets,
                }}
              />
              <div className="all-center-wrapper column-wrapper" style={{ position: "absolute" }}>
                {doughnut?.center && (
                  <Typography variant="h4">
                    {doughnut?.center?.prefix}
                    {!groups
                      ? parseChartExpression(
                          reportData,
                          doughnut?.center?.expression,
                          doughnut?.center?.decimalPlaces
                        ).toFixed(doughnut?.center?.decimalPlaces)
                      : typeof groupCenterText === "number"
                      ? groupCenterText.toFixed(doughnut?.center?.decimalPlaces)
                      : groupCenterText}
                    {doughnut?.center?.suffix}
                  </Typography>
                )}
              </div>
            </>
          )}
          {props?.loading && (
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={4} height={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </Box>
      </Paper>
    </Stack>
  );
}
